import type { ReactNode } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "@/hooks/use-workspace";
import type { Permission } from "@/lib/permissions";

interface PermissionRouteProps {
  permission: Permission;
  children: ReactNode;
}

/**
 * Gates a route by a single permission key. Renders a "No access" card
 * (NOT a redirect) when the check fails — preserves the URL so the user
 * can see what they tried to reach, and admins can grant the permission
 * without making the user retype the path.
 *
 * Layered with `<FeatureFlagRoute>` and `<ProtectedRoute>` it gives:
 *   auth (ProtectedRoute) → module enabled (FeatureFlagRoute) → member has permission (this)
 */
export function PermissionRoute({ permission, children }: PermissionRouteProps) {
  const navigate = useNavigate();
  const { currentWorkspace, isLoading, canDo } = useWorkspace();

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-md">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (!currentWorkspace) {
    // No workspace selected yet — fall through to whatever the parent renders.
    return <>{children}</>;
  }

  if (!canDo(permission)) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <Lock className="h-5 w-5 text-muted-foreground" />
            </div>
            <CardTitle>You don't have access to this section</CardTitle>
            <CardDescription>
              Ask a workspace admin to grant you the <code className="px-1 py-0.5 rounded bg-muted text-xs">{permission}</code> permission.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full" onClick={() => navigate("/dashboard")}>
              Back to dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
